import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Snackbar, Alert, Paper, Divider, MenuItem,
  Switch, FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AdminPageHeader  from "../../components/admin/AdminPageHeader";
import AdminGridToolbar from "../../components/admin/AdminGridToolbar";
import AdminFormDialog  from "../../components/admin/AdminFormDialog";
import AdminFilterPanel from "../../components/admin/AdminFilterPanel";
import RichTextEditor   from "../../components/admin/RichTextEditor";
import {
  listMessages, createMessage, updateMessage, deleteMessage,
  listAssignableCompanies,
} from "../../services/adminService";

// --- helpers ---

function startOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function endOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}

const EMPTY = {
  companyCnpj: "",
  startDate:   "",
  endDate:     "",
  type:        "HEADER",
  content:     "",
  isActive:    true,
};

const TYPE_LABELS = { HEADER: "Banner no topo", POPUP: "Pop-up" };

function fmtDate(v) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString("pt-BR");
}

function toInputDate(v) {
  if (!v) return "";
  return new Date(v).toISOString().slice(0, 10);
}

// --- component ---

export default function MessagesPage() {
  const [rows,      setRows]      = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [rowSel,    setRowSel]    = useState({ type: "include", ids: new Set() });
  const [dialogOpen,setDialogOpen]= useState(false);
  const [deleteOpen,setDeleteOpen]= useState(false);
  const [editing,   setEditing]   = useState(null);
  const [form,      setForm]      = useState(EMPTY);
  const [saving,    setSaving]    = useState(false);
  const [snack,     setSnack]     = useState({ open: false, msg: "", severity: "success" });

  // filtros
  const [fDateFrom,  setFDateFrom]  = useState(startOfMonth);
  const [fDateTo,    setFDateTo]    = useState(endOfMonth);
  const [fCompanyCnpj, setFCompanyCnpj] = useState("");

  const selectedIds = useMemo(() => Array.from(rowSel.ids), [rowSel]);
  const selectedRow = useMemo(() => rows.find(r => r.id === selectedIds[0]), [rows, selectedIds]);

  const showSnack = (msg, severity = "success") => setSnack({ open: true, msg, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listMessages();
      setRows(Array.isArray(data) ? data : []);
    } catch { showSnack("Erro ao carregar mensagens.", "error"); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    listAssignableCompanies().then(d => setCompanies(Array.isArray(d) ? d : [])).catch(() => {});
  }, [load]);

  // filtragem client-side
  const filtered = useMemo(() => {
    return rows.filter(r => {
      const start = new Date(r.start_date);
      const end   = new Date(r.end_date);
      if (fDateFrom && end   < new Date(fDateFrom)) return false;
      if (fDateTo   && start > new Date(fDateTo + "T23:59:59")) return false;
      if (fCompanyCnpj && r.company_cnpj !== fCompanyCnpj) return false;
      return true;
    });
  }, [rows, fDateFrom, fDateTo, fCompanyCnpj]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!selectedRow) return;
    setEditing(selectedRow);
    setForm({
      companyCnpj: selectedRow.company_cnpj ?? "",
      startDate:   toInputDate(selectedRow.start_date),
      endDate:     toInputDate(selectedRow.end_date),
      type:        selectedRow.type,
      content:     selectedRow.content,
      isActive:    selectedRow.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.startDate || !form.endDate) return showSnack("Datas são obrigatórias.", "error");
    if (!form.content?.trim())            return showSnack("Conteúdo é obrigatório.", "error");
    setSaving(true);
    try {
      const body = { ...form, companyCnpj: form.companyCnpj || null };
      if (editing) await updateMessage(editing.id, body);
      else         await createMessage(body);
      showSnack(editing ? "Mensagem atualizada." : "Mensagem criada.");
      setDialogOpen(false);
      load();
    } catch (err) { showSnack(err.message, "error"); }
    finally       { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await Promise.all(selectedIds.map(id => deleteMessage(id)));
      showSnack("Mensagem(ns) removida(s).");
      setDeleteOpen(false);
      setRowSel({ type: "include", ids: new Set() });
      load();
    } catch { showSnack("Erro ao remover.", "error"); }
    finally  { setSaving(false); }
  };

  const columns = [
    {
      field: "type", headerName: "Tipo", width: 130,
      valueGetter: (v) => TYPE_LABELS[v] ?? v,
    },
    {
      field: "company_cnpj", headerName: "Empresa", flex: 1,
      valueGetter: (_, row) => row.company?.company_name ?? "Todas as empresas",
    },
    { field: "start_date", headerName: "Início", width: 110, valueGetter: (v) => fmtDate(v) },
    { field: "end_date",   headerName: "Fim",    width: 110, valueGetter: (v) => fmtDate(v) },
    {
      field: "content", headerName: "Mensagem", flex: 2,
      renderCell: ({ value }) => (
        <Box sx={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          dangerouslySetInnerHTML={{ __html: value }} />
      ),
    },
  ];

  return (
    <Box sx={{ p: 2, pt: 0 }}>
      <AdminPageHeader title="Central de Mensagens" />

      {/* Filtros */}
      <AdminFilterPanel>
        <TextField
          select label="Empresa" size="small" InputLabelProps={{ shrink: true }}
          value={fCompanyCnpj}
          onChange={e => setFCompanyCnpj(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Todas as empresas</MenuItem>
          {companies.map(c => (
            <MenuItem key={c.cnpj} value={c.cnpj}>{c.company_name}</MenuItem>
          ))}
        </TextField>

        <TextField
          label="De" type="date" size="small" InputLabelProps={{ shrink: true }}
          value={fDateFrom}
          onChange={e => setFDateFrom(e.target.value)}
          sx={{ width: 150 }}
        />
        <TextField
          label="Até" type="date" size="small" InputLabelProps={{ shrink: true }}
          value={fDateTo}
          onChange={e => setFDateTo(e.target.value)}
          sx={{ width: 150 }}
        />
        <Button
          size="small"
          variant="text"
          sx={{ textTransform: "none", fontSize: 12, color: "text.secondary" }}
          onClick={() => { setFDateFrom(startOfMonth()); setFDateTo(endOfMonth()); setFCompanyCnpj(""); }}
        >
          Limpar filtros
        </Button>
      </AdminFilterPanel>

      <Paper sx={{ borderRadius: 0, border: "1px solid", borderColor: "divider" }}>
        <DataGrid
          rows={filtered} columns={columns} getRowId={r => r.id}
          loading={loading} autoHeight checkboxSelection disableRowSelectionOnClick
          density="compact" showToolbar
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          rowSelectionModel={rowSel}
          onRowSelectionModelChange={m => setRowSel({ type: "include", ids: new Set(Array.from(m?.ids ?? [])) })}
          slots={{ toolbar: AdminGridToolbar }}
          slotProps={{ toolbar: {
            title: "Mensagens",
            onNew: openNew, onEdit: openEdit, onDelete: () => setDeleteOpen(true),
            canEdit: selectedIds.length === 1, canDelete: selectedIds.length > 0,
          }}}
        />
      </Paper>

      {/* Dialog criar/editar */}
      <AdminFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleSave} saving={saving}
        title={editing ? "Editar Mensagem" : "Nova Mensagem"}>

        <TextField select label="Empresa" size="small" InputLabelProps={{ shrink: true }}
          value={form.companyCnpj} onChange={e => setForm(p => ({ ...p, companyCnpj: e.target.value }))}>
          <MenuItem value="">Todas as empresas</MenuItem>
          {companies.map(c => (
            <MenuItem key={c.cnpj} value={c.cnpj}>{c.company_name}</MenuItem>
          ))}
        </TextField>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField label="Data de início *" type="date" size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
            value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} />
          <TextField label="Data de fim *" type="date" size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
            value={form.endDate} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))} />
        </Box>

        <TextField select label="Tipo de exibição *" size="small" InputLabelProps={{ shrink: true }}
          value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
          <MenuItem value="HEADER">Banner no topo</MenuItem>
          <MenuItem value="POPUP">Pop-up</MenuItem>
        </TextField>

        <Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>Mensagem *</Typography>
          <RichTextEditor
            value={form.content}
            onChange={v => setForm(p => ({ ...p, content: v }))}
            placeholder="Digite o conteúdo da mensagem..."
          />
        </Box>

        {editing && (
          <FormControlLabel
            control={<Switch checked={!!form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />}
            label="Mensagem ativa" />
        )}
      </AdminFormDialog>

      {/* Dialog confirmar exclusão */}
      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: 0 } } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 14, pb: 1 }}>Remover mensagem</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {selectedIds.length > 1
              ? `Remover ${selectedIds.length} mensagens?`
              : `Remover a mensagem selecionada?`}
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.2, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving} sx={{ textTransform: "none", fontSize: 13 }}>Cancelar</Button>
          <Button onClick={handleDelete} disabled={saving} sx={{ textTransform: "none", fontSize: 13, color: "#d32f2f" }}>
            {saving ? "Removendo..." : "Remover"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
